/**
 * Company field for new-record (quick-create) forms on S / S? documents —
 * invoices, purchase orders, sales orders, journal entries.
 *
 * Implements the create-time company precedence from
 * docs/platform/multi-company-design.md §"Creating a new record — company
 * precedence":
 *
 *   1. Parent/context company wins — when created from a linked record the
 *      company is inherited and shown as a read-only "Creating in X" chip.
 *   2. Else the global scope, if a specific company — pre-filled and shown as a
 *      changeable "Creating in X · Change" chip (one-click happy path, but
 *      visible and correctable). Clicking Change reveals the picker.
 *   3. Else (scope = All companies) — the required company picker is shown.
 *   4. Single-company tenant — nothing is rendered (the dimension is implicit).
 *
 * Distinct from the on-record `DocumentCompanyField` (which edits an existing
 * draft's company) and from the app-bar `CompanyScopeSwitcher` (the global
 * filter). This is the creation-time affordance only.
 *
 * Controlled: the parent owns `value` (companies.id, '' = none) and the submit
 * payload. Use `useCompanyCreatePrecedence` to seed the initial value
 * (`defaultCompanyId`) and to gate submit (`requiresPick`).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Pencil } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useCompanyScope } from '../../contexts/CompanyScopeContext';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';
import { CompanyPicker } from '../common/CompanyPicker';

export interface CompanyCreatePrecedence {
  /** Accessible companies for the current user. */
  companies: Array<{ id: string; name: string }>;
  /** True when the tenant exposes ≥2 companies (the dimension is visible). */
  multiCompany: boolean;
  /**
   * The company id to pre-fill a new record with: the context/parent company
   * if any, else the specific global scope, else '' (must pick / single-co).
   */
  defaultCompanyId: string;
  /** Scope = "All companies" and no context → the picker is required. */
  requiresPick: boolean;
  /** A parent/context company was supplied — it wins and is read-only. */
  contextLocked: boolean;
}

/**
 * Resolve the create-time company precedence for a quick-create form.
 *
 * The parent uses `defaultCompanyId` to seed its form value on open and
 * `requiresPick` / `multiCompany` to gate submit — typically
 * `if (multiCompany && !companyId) return;` (pre-fill makes the specific-scope
 * case a one-click happy path; the All-companies case blocks until a pick).
 */
export function useCompanyCreatePrecedence(
  contextCompanyId?: string | null,
): CompanyCreatePrecedence {
  const { data } = useMyCompanies();
  const { companyScopeId } = useCompanyScope();
  const companies = data?.data ?? [];
  const multiCompany = companies.length > 1;

  const context = contextCompanyId ?? '';
  const contextLocked = !!context;
  // Specific scope (not "All") pre-fills; context overrides scope entirely.
  const scopeDefault = !contextLocked && companyScopeId ? companyScopeId : '';
  // Single-company tenant: the sole company is implicit (precedence step 4 —
  // "always auto, never shown"). Seed it so the create payload carries a
  // company_id even under an "All companies" scope; otherwise the backend
  // rejects the quick-create with 400 "company_id is required".
  const soleCompanyId = companies.length === 1 ? companies[0].id : '';
  const defaultCompanyId = context || scopeDefault || soleCompanyId;

  return {
    companies,
    multiCompany,
    defaultCompanyId,
    requiresPick: multiCompany && !defaultCompanyId,
    contextLocked,
  };
}

export interface CompanyCreateFieldProps {
  /** Selected company id; '' = none (submit blocks while a pick is required). */
  value: string;
  onChange: (companyId: string) => void;
  /**
   * Parent/context company id — when set, the company is inherited from a
   * linked record (precedence step 1) and shown read-only.
   */
  contextCompanyId?: string | null;
  /** Field label override; defaults to the tenant `{{Company}}` term. */
  label?: string;
  /** Show the picker in an error state (only meaningful in picker mode). */
  error?: boolean;
  /** Error message rendered under the picker when {@link error} is true. */
  errorMessage?: string;
  className?: string;
}

export function CompanyCreateField({
  value,
  onChange,
  contextCompanyId,
  label,
  error,
  errorMessage,
  className,
}: CompanyCreateFieldProps) {
  const { t } = useTranslation('common');
  const { companies, multiCompany, requiresPick, contextLocked } =
    useCompanyCreatePrecedence(contextCompanyId);

  // While the chip is shown the user may click "Change" to reveal the picker.
  const [changing, setChanging] = useState(false);
  // Reset the local toggle whenever the resolved company changes (dialog open
  // seeds a new value) so a reused dialog never reopens stuck in picker mode.
  useEffect(() => {
    setChanging(false);
  }, [value, contextCompanyId]);

  // Single-company tenant: the dimension is implicit, render nothing.
  if (!multiCompany) return null;

  const resolvedLabel = label ?? t('common:multiCompany.company');
  const selected = companies.find((c) => c.id === value) ?? null;

  // Step 3 / after "Change": the required picker (All-companies scope).
  if (requiresPick || changing) {
    return (
      <CompanyPicker
        value={value || null}
        onChange={(id) => onChange(id ?? '')}
        label={resolvedLabel}
        required
        error={error}
        errorMessage={errorMessage}
        className={className}
      />
    );
  }

  // Steps 1–2: a chip showing the inherited/pre-filled company.
  const companyName = selected?.name ?? '—';
  const creatingInLabel = t('multiCompany.creatingIn');
  return (
    <div
      role="group"
      aria-label={`${creatingInLabel} ${companyName}`}
      className={cn(
        'inline-flex max-w-full items-center gap-2 rounded-md border border-border bg-muted/40 px-2.5 py-1.5 text-sm',
        className,
      )}
    >
      <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="min-w-0 truncate">
        <span className="text-muted-foreground">{creatingInLabel} </span>
        <span className="font-medium text-foreground">{companyName}</span>
      </span>
      {!contextLocked && (
        <>
          <span className="text-muted-foreground" aria-hidden="true">
            ·
          </span>
          {/* Inline text affordance; -my-1.5 widens the tap area (~44px) to the
              chip edges without growing the chip's visual height. */}
          <button
            type="button"
            onClick={() => setChanging(true)}
            className="-my-1.5 inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-2 font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Pencil className="h-3 w-3" aria-hidden="true" />
            {t('multiCompany.change')}
          </button>
        </>
      )}
    </div>
  );
}
